import { useEffect, useState } from "react";
import styled from "styled-components";

import { VoteStatus } from "types/voteEnum";

import OptionsWrapper from "./OptionsWrapperDnD";
import ActionSection from "../../common/ActionSection";
import TitleHeader from "../../common/TitleHeader";
import {
    createInitialAnswerStatus,
    isAnswerLocked,
} from "components/vote/common/answerLock";

type Props = {
    onValidate: (answer: string | Array<string>) => Promise<{
        [key: string]: any;
    }>;
    options: Array<{
        id: string;
        text: string;
    }>;
    title: string;
    canAnswerBeUpdated: boolean;
    hasCurrentUserAnswered: boolean;
};

const OrderedQuestion = ({
    title,
    options,
    onValidate,
    canAnswerBeUpdated,
    hasCurrentUserAnswered,
}: Props) => {
    const [listOptions, setListOptions] = useState(options);
    const [voteStatus, setVoteStatus] = useState(
        createInitialVoteStatus(hasCurrentUserAnswered),
    );

    const answerIsLocked = isAnswerLocked(
        voteStatus,
        hasCurrentUserAnswered,
        canAnswerBeUpdated,
    );

    useEffect(() => {
        // A new title means a new question: only then is the order reset.
        setListOptions(options);
        setVoteStatus(createInitialVoteStatus(hasCurrentUserAnswered));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, hasCurrentUserAnswered]);

    const handleValidate = async () => {
        if (answerIsLocked) {
            return;
        }

        const mapListAnswers = listOptions.map((state) => state.id);
        const answer = mapListAnswers.join("");
        try {
            await onValidate(answer);
            setVoteStatus(VoteStatus.Success);
        } catch {
            setVoteStatus(VoteStatus.Error);
        }
    };

    const handleReset = () => {
        setVoteStatus(VoteStatus.PendingValidation);
    };

    return (
        <WrapperVote>
            <TitleHeader title={title} />
            <OptionsWrapper
                options={listOptions}
                setOptions={setListOptions}
                setVoteStatus={setVoteStatus}
                isLocked={answerIsLocked}
            />
            <ActionSection
                handleValidate={handleValidate}
                handleReset={handleReset}
                voteStatus={voteStatus}
                canAnswerBeUpdated={canAnswerBeUpdated}
            />
        </WrapperVote>
    );
};

const WrapperVote = styled.div`
    width: 100%;
    margin: auto;
    @media (max-width: 768px) {
        max-width: 350px;
    }
    overscroll-behavior-y: contain;
    touch-action: pan-x;
`;

function createInitialVoteStatus(hasCurrentUserAnswered: boolean): VoteStatus {
    return createInitialAnswerStatus(
        hasCurrentUserAnswered,
        VoteStatus.PendingValidation,
    );
}

export default OrderedQuestion;
